import { datadogRum } from '@datadog/browser-rum'

class DatadogService {
  constructor() {
    this.isInitialized = false
    this.isEnabled = false
  }

  /**
   * Initialize Datadog RUM based on environment configuration
   * @param {Object} config - Configuration object
   * @param {string} config.userEmail - User's email address
   * @param {string} config.userName - User's full name
   * @param {string} config.environment - Environment (dev, uat, prod)
   * @param {Object} config.officeContext - Office context information
   */
  init(config = {}) {
    try {
      // Check if Datadog should be enabled
      this.isEnabled = process.env.NODE_ENV === 'production' || 
                      process.env.VITE_ENABLE_DATADOG === 'true'

      if (!this.isEnabled) {
        console.log('Datadog RUM disabled for this environment')
        return
      }

      console.log('Initializing Datadog RUM...')

      // Ensure required environment variables are present
      const applicationId = process.env.VITE_DATADOG_APPLICATION_ID
      const clientToken = process.env.VITE_DATADOG_CLIENT_TOKEN
      const environment = process.env.VITE_APP_ENV || 'UAT'

      if (!applicationId || !clientToken) {
        console.warn('Datadog RUM: Missing required environment variables')
        return
      }

      console.log('Datadog config:', {
        applicationId,
        clientToken: clientToken.substring(0, 10) + '...', // Log partial token for security
        environment,
        service: 'vas-tops2-outlook-addin'
      })

      // Initialize Datadog RUM with environment configuration
      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: 'vas-tops2-outlook-addin',
        env: environment,
        version: process.env.MANIFEST_VERSION || '1.0.0.3',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        defaultPrivacyLevel: 'mask-user-input',
        trackInteractions: true,
        // Outlook Add-in specific configurations
        trackViewTransitions: false, // Add-ins don't have traditional page transitions
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
      })

      // Start session replay recording
      datadogRum.startSessionReplayRecording()

      // Add CBRE-specific global context
      datadogRum.addRumGlobalContext('cbre_details', {
        tower: 'global_advisory',
        lob: 'vas',
        application: 'vas_tops2_outlook_addin',
        apm_id: process.env.VITE_DATADOG_APM_ID || null,
      })

      // Add Outlook-specific context
      this.addOutlookContext(config.officeContext)

      // Set user information if provided
      if (config.userEmail) {
        const userInfo = config.userName ? { name: config.userName } : {}
        this.setUser(config.userEmail, userInfo)
      }

      this.isInitialized = true
      console.log('Datadog RUM initialized successfully for Outlook Add-in')

      // Mark as initialized
      this.isInitialized = true
      console.log('✅ Datadog RUM initialized successfully!')
      
      // Send initialization event
      this.addAction('datadog_initialization', {
        timestamp: new Date().toISOString(),
        environment
      })

    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error)
    }
  }

  /**
   * Set user information for tracking
   * @param {string} email - User's email address
   * @param {Object} additionalInfo - Additional user information
   */
  setUser(email, additionalInfo = {}) {
    if (!this.isInitialized || !this.isEnabled) return

    try {
      // Datadog requires specific field names for user tracking:
      // - id: Unique identifier for the user (required for session tracking)
      // - name: User's display name
      // - email: User's email address
      const userObject = {
        id: email, // Use email as unique identifier
        email: email,
        name: additionalInfo.name || email.split('@')[0], // Fallback to email username if name not provided
        ...additionalInfo
      }
      
      datadogRum.setUser(userObject)
      
      console.log('✅ Datadog user set successfully')
    } catch (error) {
      console.error('Failed to set Datadog user:', error)
    }
  }

  /**
   * Add Outlook-specific context information
   * @param {Object} officeContext - Office.js context information
   */
  addOutlookContext(officeContext = {}) {
    if (!this.isInitialized || !this.isEnabled) return

    try {
      const outlookContext = {
        outlook_version: this.getOutlookVersion(),
        office_host: officeContext.host || 'unknown',
        platform: this.getPlatform(),
        add_in_version: process.env.MANIFEST_VERSION || '1.0.0',
      }

      datadogRum.addRumGlobalContext('outlook_context', outlookContext)
    } catch (error) {
      console.error('Failed to add Outlook context:', error)
    }
  }

  /**
   * Track custom action in the add-in
   * @param {string} actionName - Name of the action
   * @param {Object} context - Additional context for the action
   */
  addAction(actionName, context = {}) {
    if (!this.isInitialized || !this.isEnabled) return

    try {
      datadogRum.addAction(actionName, context)
    } catch (error) {
      console.error('Failed to add Datadog action:', error)
    }
  }

  /**
   * Add custom error tracking
   * @param {Error} error - The error to track
   * @param {Object} context - Additional context
   */
  addError(error, context = {}) {
    if (!this.isInitialized || !this.isEnabled) return

    try {
      datadogRum.addError(error, context)
    } catch (error) {
      console.error('Failed to add Datadog error:', error)
    }
  }

  /**
   * Add timing information for performance tracking
   * @param {string} name - Name of the timing
   * @param {number} duration - Duration in milliseconds
   * @param {Object} context - Additional context
   */
  addTiming(name, duration, context = {}) {
    if (!this.isInitialized || !this.isEnabled) return

    try {
      datadogRum.addTiming(name, duration, context)
    } catch (error) {
      console.error('Failed to add Datadog timing:', error)
    }
  }

  /**
   * Track email-related actions
   * @param {string} action - The email action (view, process, save, etc.)
   * @param {Object} emailContext - Email-related context
   */
  trackEmailAction(action, emailContext = {}) {
    const context = {
      email_action: action,
      has_email_id: !!emailContext.emailId,
      has_assignment: !!emailContext.assignmentId,
      ...emailContext
    }

    this.addAction(`email_${action}`, context)
  }

  /**
   * Track TOPS2-specific actions
   * @param {string} action - The TOPS2 action
   * @param {Object} actionContext - Action-related context
   */
  trackTOPS2Action(action, actionContext = {}) {
    const context = {
      tops2_action: action,
      ...actionContext
    }

    this.addAction(`tops2_${action}`, context)
  }

  /**
   * Get Outlook version information
   * @returns {string} Outlook version
   */
  getOutlookVersion() {
    try {
      if (typeof Office !== 'undefined' && Office.context) {
        return Office.context.diagnostics?.version || 'unknown'
      }
      return 'unknown'
    } catch (error) {
      return 'unknown'
    }
  }

  /**
   * Get platform information
   * @returns {string} Platform information
   */
  getPlatform() {
    try {
      if (typeof Office !== 'undefined' && Office.context) {
        return Office.context.diagnostics?.platform || 'unknown'
      }
      return navigator.platform || 'unknown'
    } catch (error) {
      return 'unknown'
    }
  }

  /**
   * Check if Datadog is enabled and initialized
   * @returns {boolean} True if enabled and initialized
   */
  isReady() {
    return this.isInitialized && this.isEnabled
  }
}

// Export singleton instance
export default new DatadogService()
